export const SESSION_HEADER = 'X-Session-Token';
const SESSION_STORAGE_KEY = 'rh_session_token';
const AUTH_EXPIRED_EVENT = 'rh-auth-expired';
const AUTH_EXPIRED_OPT_OUT_HEADER = 'X-Skip-Auth-Expired';
const DEV_FETCH_DEBUG_KEY = '__rhFetchDebug';
let devFetchAutoLogTimer: number | null = null;

const DEFAULT_AUTH_FETCH_TIMEOUT_MS = 45_000;

export type LastAuthFetchRequest = {
  ts: number;
  url: string;
  method: string;
  usedToken: boolean;
};

export type AuthFetchDebugSnapshot = {
  count: number;
  method: string;
  url: string;
  lastMsAgo: number;
  status?: number;
  ok?: boolean;
  durationMs?: number;
  error?: string;
};

let lastAuthFetchRequest: LastAuthFetchRequest | null = null;

export function readLastAuthFetchRequest(): LastAuthFetchRequest | null {
  return lastAuthFetchRequest;
}

function readFromStorage(): string {
  if (typeof window === 'undefined' || !window?.localStorage) {
    return '';
  }
  try {
    return window.localStorage.getItem(SESSION_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function writeToStorage(token: string) {
  if (typeof window === 'undefined' || !window?.localStorage) {
    return;
  }
  try {
    if (token) {
      window.localStorage.setItem(SESSION_STORAGE_KEY, token);
    } else {
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
    }
  } catch {
    // Ignore storage failures (e.g., Safari private mode)
  }
}

let cachedSessionToken = readFromStorage();
let reportedAuthExpiredToken = '';

export function setAuthSessionToken(token: string): void {
  cachedSessionToken = token?.trim() || '';
  writeToStorage(cachedSessionToken);
  reportedAuthExpiredToken = '';
}

export function clearAuthSessionToken(): void {
  cachedSessionToken = '';
  writeToStorage('');
  reportedAuthExpiredToken = '';
}

export function readSessionToken(): string {
  if (cachedSessionToken) {
    return cachedSessionToken;
  }
  cachedSessionToken = readFromStorage().trim();
  return cachedSessionToken;
}

export type AuthFetchInit = RequestInit & { timeoutMs?: number };

function combineSignals(signals: Array<AbortSignal | undefined>): AbortSignal | undefined {
  const available = signals.filter(Boolean) as AbortSignal[];
  if (available.length === 0) return undefined;
  if (available.length === 1) return available[0];

  const anyFn = (AbortSignal as unknown as { any?: (signals: AbortSignal[]) => AbortSignal }).any;
  if (typeof anyFn === 'function') {
    return anyFn(available);
  }

  const controller = new AbortController();
  const onAbort = () => controller.abort();
  available.forEach((signal) => {
    if (signal.aborted) {
      controller.abort();
      return;
    }
    try {
      signal.addEventListener('abort', onAbort, { once: true });
    } catch {
      // Ignore addEventListener failures (very old environments)
    }
  });
  return controller.signal;
}

function isApiPath(pathname: string): boolean {
  const candidate = (pathname || '').trim();
  return candidate === '/api' || candidate.startsWith('/api/');
}

export function authFetch(input: RequestInfo | URL, init: AuthFetchInit = {}): Promise<Response> {
  const { timeoutMs, signal: providedSignal, ...restInit } = init;
  const normalizedSignal = providedSignal ?? undefined;
  const headers = new Headers(restInit.headers || {});
  const sessionToken = readSessionToken();
  const urlString = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
  const tokenUsed = sessionToken?.trim() || '';
  const methodUsed = (restInit.method || 'GET').toUpperCase();
  
  // Always set session token if available and not already present
  // This ensures the token is sent with every request
  if (sessionToken && !headers.has(SESSION_HEADER)) {
    headers.set(SESSION_HEADER, sessionToken);
  }
  
  // Ensure JSON content type when sending a string body and header is not already present
  if (restInit.body && typeof restInit.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  
  const credentials = restInit.credentials ?? 'include';

  const resolvedTimeoutMsRaw =
    typeof timeoutMs === 'number' && Number.isFinite(timeoutMs)
      ? timeoutMs
      : normalizedSignal
        ? null
        : DEFAULT_AUTH_FETCH_TIMEOUT_MS;
  const resolvedTimeoutMs =
    typeof resolvedTimeoutMsRaw === 'number' && resolvedTimeoutMsRaw > 0 ? resolvedTimeoutMsRaw : null;

  const timeoutController = resolvedTimeoutMs ? new AbortController() : null;
  const signal = timeoutController ? combineSignals([normalizedSignal, timeoutController.signal]) : normalizedSignal;

  const finalInit: RequestInit = { ...restInit, headers, credentials, signal };

  lastAuthFetchRequest = {
    ts: Date.now(),
    url: urlString,
    method: methodUsed,
    usedToken: Boolean(tokenUsed),
  };

  const debugEntry = recordDevFetchDebug(input, finalInit);

  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  if (timeoutController && resolvedTimeoutMs) {
    timeoutId = setTimeout(() => timeoutController.abort(), resolvedTimeoutMs);
  }

  return fetch(input, finalInit)
    .then((response) => {
      if (debugEntry) {
        debugEntry.status = response.status;
        debugEntry.ok = response.ok;
        debugEntry.durationMs = debugEntry.startedTs ? Date.now() - debugEntry.startedTs : undefined;
        debugEntry.lastTs = Date.now();
      }
      // Centralized auth-expired handling:
      // Only trigger when we actually used a token, the response is 401,
      // and the token hasn't changed since the request was issued (avoid logout races).
      if (
        response.status === 401 &&
        tokenUsed &&
        tokenUsed === readSessionToken() &&
        tokenUsed !== reportedAuthExpiredToken &&
        !headers.has(AUTH_EXPIRED_OPT_OUT_HEADER)
      ) {
        let shouldSkip = false;
        try {
          const parsed = new URL(
            urlString,
            typeof window !== 'undefined' ? window.location.origin : 'http://localhost',
          );
          // Only treat API responses as session-expired signals.
          shouldSkip = !isApiPath(parsed.pathname);
          // Never treat login credential failures as a session-expired signal.
          shouldSkip = shouldSkip || parsed.pathname.endsWith('/api/users/login');
        } catch {
          // Best-effort; if URL parsing fails, proceed.
        }

        if (!shouldSkip) {
          reportedAuthExpiredToken = tokenUsed;
          try {
            if (typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent(AUTH_EXPIRED_EVENT, {
                  detail: {
                    status: 401,
                    url: urlString,
                    method: methodUsed,
                    reason: 'unauthorized',
                  },
                }),
              );
            }
          } catch {
            // Ignore event dispatch failures; request caller still receives the 401 response.
          }
        }
      }
      return response;
    })
    .catch((error) => {
      if (debugEntry) {
        debugEntry.error = error instanceof Error ? error.message : 'fetch failed';
        debugEntry.durationMs = debugEntry.startedTs ? Date.now() - debugEntry.startedTs : undefined;
        debugEntry.lastTs = Date.now();
      }
      throw error;
    })
    .finally(() => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    });
}

type FetchDebugEntry = {
  method: string;
  url: string;
  count: number;
  lastTs: number;
  startedTs?: number;
  status?: number;
  ok?: boolean;
  durationMs?: number;
  error?: string;
};

type FetchDebugStore = {
  entries: Map<string, FetchDebugEntry>;
  logTop: (limit?: number) => void;
  reset: () => void;
};

function toDebugSnapshot(entry: FetchDebugEntry): AuthFetchDebugSnapshot {
  return {
    count: entry.count,
    method: entry.method,
    url: entry.url,
    lastMsAgo: Date.now() - entry.lastTs,
    status: entry.status,
    ok: entry.ok,
    durationMs: entry.durationMs,
    error: entry.error,
  };
}

function createFetchDebugStore(): FetchDebugStore {
  const entries = new Map<string, FetchDebugEntry>();

  return {
    entries,
    logTop(limit = 20) {
      const rows = Array.from(entries.values())
        .sort((a, b) => b.count - a.count)
        .slice(0, limit)
        .map(toDebugSnapshot);
      if (rows.length) {
        // eslint-disable-next-line no-console
        console.table(rows);
      } else {
        // eslint-disable-next-line no-console
        console.info('[fetch-debug] No entries recorded yet.');
      }
    },
    reset() {
      entries.clear();
    },
  };
}

function getDevFetchDebugStore(): FetchDebugStore | null {
  if (import.meta.env?.PROD) {
    return null;
  }
  const globalScope: Record<string, unknown> = typeof window !== 'undefined' ? window : (globalThis as any);
  if (!globalScope) {
    return null;
  }
  const existing = globalScope[DEV_FETCH_DEBUG_KEY];
  if (existing && typeof existing === 'object') {
    return existing as FetchDebugStore;
  }
  const store = createFetchDebugStore();
  Object.defineProperty(globalScope, DEV_FETCH_DEBUG_KEY, {
    configurable: false,
    enumerable: false,
    writable: false,
    value: store,
  });
  return store;
}

export function readAuthFetchDebugSnapshot(limit = 12): AuthFetchDebugSnapshot[] {
  const store = getDevFetchDebugStore();
  if (!store) {
    return [];
  }
  return Array.from(store.entries.values())
    .sort((a, b) => b.lastTs - a.lastTs)
    .slice(0, limit)
    .map(toDebugSnapshot);
}

function normalizeDebugUrl(input: RequestInfo | URL): { method: string; url: string } {
  let urlString: string;
  if (typeof input === 'string') {
    urlString = input;
  } else if (input instanceof URL) {
    urlString = input.toString();
  } else {
    urlString = input.url;
  }

  let normalized = urlString;
  try {
    const url = new URL(urlString, typeof window !== 'undefined' ? window.location.origin : undefined);
    normalized = `${url.pathname}${url.search}`;
  } catch {
    // Ignore parse failures; fall back to raw string.
  }

  return { method: 'GET', url: normalized };
}

function recordDevFetchDebug(input: RequestInfo | URL, init: RequestInit): FetchDebugEntry | null {
  const store = getDevFetchDebugStore();
  if (!store) {
    return null;
  }

  const { method: defaultMethod, url } = normalizeDebugUrl(input);
  const method = (init.method || defaultMethod).toUpperCase();
  const key = `${method} ${url}`;
  const entry = store.entries.get(key);
  const now = Date.now();
  if (entry) {
    entry.count += 1;
    entry.lastTs = now;
    entry.startedTs = now;
    entry.status = undefined;
    entry.ok = undefined;
    entry.durationMs = undefined;
    entry.error = undefined;
  } else {
    store.entries.set(key, {
      method,
      url,
      count: 1,
      lastTs: now,
      startedTs: now,
    });
  }

  scheduleDevFetchAutoLog(store);
  return store.entries.get(key) ?? null;
}

function scheduleDevFetchAutoLog(store: FetchDebugStore) {
  if (typeof window === 'undefined') {
    return;
  }
  if (devFetchAutoLogTimer !== null) {
    window.clearTimeout(devFetchAutoLogTimer);
  }
  devFetchAutoLogTimer = window.setTimeout(() => {
    store.logTop(30);
  }, 1500);
}
